import { useTranslation } from "react-i18next";
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@nexus/ui";

/**
 * Widgets that carry a plain-English explainer. Each maps to `preflight.help.<topic>` copy with
 * a title and the three questions a first-time reader has, in the order they have them.
 */
export type WidgetHelpTopic =
  | "spread"
  | "composition"
  | "blockers"
  | "findings"
  | "trends"
  | "heatmap"
  | "health"
  | "scoreTrend"
  | "sources";

/** Asked in this order: what am I looking at, how do I read it, what do I do about it. */
const SECTIONS = ["shows", "read", "use"] as const;

export interface WidgetHelpProps {
  topic: WidgetHelpTopic;
  className?: string;
}

/**
 * "What is this?" for one widget: what it shows, how to read it, and what to do with it, in
 * words that assume no knowledge of the scoring model.
 *
 * A popover rather than a tooltip — the copy is three sentences a user needs to read at their
 * own pace, and hover text is unreachable on touch. The trigger is deliberately quiet: an
 * explainer that competes with the data it explains is a worse chart.
 */
export function WidgetHelp({ topic, className }: WidgetHelpProps) {
  const { t } = useTranslation();
  const title = t(`preflight.help.${topic}.title`);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn(
            "text-muted-foreground hover:text-foreground",
            className,
          )}
          aria-label={t("preflight.help.triggerAria", { widget: title })}
        >
          <Icon name="info" size="sm" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="text-foreground text-sm font-semibold">{title}</p>
        <dl className="mt-3 flex flex-col gap-3">
          {SECTIONS.map((section) => (
            <div key={section} className="flex flex-col gap-1">
              <dt className="text-muted-foreground font-mono text-[10px] tracking-widest uppercase">
                {t(`preflight.help.${section}Label`)}
              </dt>
              <dd className="text-foreground/90 text-sm leading-relaxed">
                {t(`preflight.help.${topic}.${section}`)}
              </dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}
